import { Flex, Text } from '@chakra-ui/react';
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, useToast } from '@morressier/alembic';
import { IArticleType } from '@publishing-engine/domain/models';
import { MANDATORY_AUTHOR_SUBMISSION_FIELDS } from '@publishing-engine/submission-service/src/fixtures/mandatory-form-fields';
import { FormFieldDefinition } from '@publishing-engine/domain/types/article';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useUpdateArticleTypeConfig } from 'src/api/mutations';
import {
  checkArticleTypeHasSubmissions,
  getAllFieldsSchema,
  getDocumentSchema
} from 'src/api/queries';
import { Layout } from 'src/components/Layout';
import { Overlay } from 'src/components/Overlay';
import NavbarDashboard from 'src/components/NavbarDashboard';
import { useWorkspace } from '../../SubmissionContexts/Users/userHook';
import AddFieldModal from './components/AddFieldModal';
import EditFieldModal from './components/EditFieldModal';
import FieldConfigTable from './components/FieldConfigTable';
import DeleteFieldModal from './components/DeleteFieldModal';
import ExitWithoutSavingWarningModal from './components/ExitWithoutSavingWarningModal';
import {
  ArticleTypeConfigField,
  FieldEditorModalState,
  FieldEditorSubmit,
  SourceFieldOption
} from './helpers/articleTypeConfig.types';
import {
  dedupeSourceFieldOptions,
  getCaseDataMainPath,
  getDependentFieldsByParentPath,
  getDependentParentFieldByFieldName,
  isDependentField,
  mapFormFieldToRow,
  mapFormFieldToSourceOption
} from './helpers/articleTypeConfig.utils';
import { EXTRACTION_SKIP_CHECKBOX_FIELD_NAME } from './components/FieldEditorModal/helpers';

interface Props {
  articleType: Pick<IArticleType, 'id' | 'name' | 'description'>;
  onClose: () => void;
  activeJournalId?: string;
}

type DeleteModalState = {
  isOpen: boolean;
  rowId?: string;
  mode?: 'single' | 'bulk';
};

const normalizeFieldName = (value: string) => value.trim().toLowerCase();
const hasDependantKind = (kind?: string) => {
  const normalizedKind = normalizeFieldName(kind || '');
  return normalizedKind.includes('dependant');
};

const MANDATORY_FIELD_NAMES = new Set(
  MANDATORY_AUTHOR_SUBMISSION_FIELDS.map(field => normalizeFieldName(field))
);

const ArticleTypeConfigModal: React.FC<Props> = ({
  articleType,
  onClose,
  activeJournalId
}) => {
  const toast = useToast();
  const { orgId, submissionContextId } = useWorkspace();
  const activeSubmissionContextId = activeJournalId || submissionContextId;

  const [fields, setFields] = useState<ArticleTypeConfigField[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [hasInitializedFields, setHasInitializedFields] = useState(false);

  const [fieldModal, setFieldModal] = useState<FieldEditorModalState>({
    isOpen: false,
    mode: 'add'
  });

  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({
    isOpen: false
  });

  const updateArticleTypeConfigMutation = useUpdateArticleTypeConfig(
    activeSubmissionContextId,
    articleType.id
  );

  const { data: hasSubmissions } = useQuery({
    queryKey: ['has-submissions', articleType.id, orgId],
    queryFn: () =>
      articleType.id && orgId
        ? checkArticleTypeHasSubmissions(orgId, articleType.id)
        : null,
    enabled: Boolean(articleType.id && orgId)
  });

  const hasAssociatedSubmissions = hasSubmissions?.hasSubmissions;

  const { data: documentSchemaResponse, isLoading: isDocumentSchemaLoading } =
    useQuery({
      queryKey: ['document-schema', articleType.id, activeSubmissionContextId],
      queryFn: () =>
        articleType.id && activeSubmissionContextId
          ? getDocumentSchema(articleType.id, activeSubmissionContextId)
          : null,
      enabled: Boolean(articleType.id && activeSubmissionContextId)
    });

  const { data: allFieldsSchemaResponse } = useQuery({
    queryKey: ['all-fields-schema', orgId],
    queryFn: () => (orgId ? getAllFieldsSchema(orgId) : null),
    enabled: Boolean(orgId)
  });

  const documentSchema = useMemo(
    () => documentSchemaResponse?.documentSchema ?? null,
    [documentSchemaResponse]
  );

  const formFields = useMemo(
    () => (documentSchema?.formFields ?? []) as FormFieldDefinition[],
    [documentSchema?.formFields]
  );

  const allAvailableFormFields = useMemo<FormFieldDefinition[]>(() => {
    return Object.values(allFieldsSchemaResponse?.fields ?? {});
  }, [allFieldsSchemaResponse]);

  const allDependentFieldsByParentPath = useMemo(
    () => getDependentFieldsByParentPath(allAvailableFormFields),
    [allAvailableFormFields]
  );

  const documentDependentParentFieldByName = useMemo(
    () => getDependentParentFieldByFieldName(formFields),
    [formFields]
  );

  const allDependentParentFieldByName = useMemo(
    () => getDependentParentFieldByFieldName(allAvailableFormFields),
    [allAvailableFormFields]
  );

  const allSourceFieldOptions = useMemo<SourceFieldOption[]>(
    () => allAvailableFormFields.map(mapFormFieldToSourceOption),
    [allAvailableFormFields]
  );

  const sourceFieldDefinitionByName = useMemo(() => {
    const mapped = new Map<string, FormFieldDefinition>();

    [...allAvailableFormFields, ...formFields].forEach(field => {
      mapped.set(normalizeFieldName(field.name), field);
    });

    return mapped;
  }, [allAvailableFormFields, formFields]);

  const fieldComponentByName = useMemo(() => {
    const mapped = new Map<string, string>();

    sourceFieldDefinitionByName.forEach((fieldDefinition, fieldName) => {
      mapped.set(fieldName, fieldDefinition.component);
    });

    return mapped;
  }, [sourceFieldDefinitionByName]);

  useEffect(() => {
    setFields([]);
    setSelectedIds(new Set());
    setHasInitializedFields(false);
    setFieldModal({ isOpen: false, mode: 'add' });
    setDeleteModal({ isOpen: false });
  }, [articleType.id]);

  useEffect(() => {
    if (hasInitializedFields || isDocumentSchemaLoading || !documentSchema) {
      return;
    }

    const initialParentRows = formFields
      .filter(field => !isDependentField(field))
      .map(mapFormFieldToRow);

    const parentRowIdByFieldName = new Map(
      initialParentRows.map(row => [normalizeFieldName(row.field), row.id])
    );

    const initialDependentRows = formFields
      .filter(field => isDependentField(field))
      .map(mapFormFieldToRow)
      .flatMap<ArticleTypeConfigField>(row => {
        const parentFieldName = documentDependentParentFieldByName.get(
          normalizeFieldName(row.field)
        );

        const parentRowId = parentFieldName
          ? parentRowIdByFieldName.get(normalizeFieldName(parentFieldName))
          : undefined;

        if (parentRowId) {
          return [{ ...row, parentRowId }];
        }

        if (hasDependantKind(row.kind)) {
          return [row];
        }

        return [];
      });

    setFields([...initialParentRows, ...initialDependentRows]);
    setHasInitializedFields(true);
  }, [
    documentDependentParentFieldByName,
    documentSchema,
    formFields,
    hasInitializedFields,
    isDocumentSchemaLoading
  ]);

  const sourceFieldOptions = useMemo<SourceFieldOption[]>(() => {
    return allSourceFieldOptions.map(option => {
      const sourcePath = getCaseDataMainPath(option.source);
      const dependentFieldNames = sourcePath
        ? allDependentFieldsByParentPath.get(sourcePath)
        : undefined;

      return {
        ...option,
        dependentFieldFromReference: dependentFieldNames
          ? Array.from(dependentFieldNames).join(', ')
          : ''
      };
    });
  }, [allDependentFieldsByParentPath, allSourceFieldOptions]);

  const dependentSourceFieldsByParentField = useMemo(() => {
    const sourceOptionByFieldName = new Map(
      allSourceFieldOptions.map(option => [
        normalizeFieldName(option.field),
        option
      ])
    );

    const mapped = new Map<string, SourceFieldOption[]>();

    allDependentParentFieldByName.forEach(
      (parentFieldName, dependentFieldName) => {
        const dependentSource = sourceOptionByFieldName.get(dependentFieldName);
        if (!dependentSource) return;

        const parentFieldKey = normalizeFieldName(parentFieldName);
        const existing = mapped.get(parentFieldKey) ?? [];

        const alreadyIncluded = existing.some(
          item =>
            normalizeFieldName(item.field) ===
            normalizeFieldName(dependentSource.field)
        );

        if (alreadyIncluded) return;

        mapped.set(parentFieldKey, [...existing, dependentSource]);
      }
    );

    return mapped;
  }, [allDependentParentFieldByName, allSourceFieldOptions]);

  const parentRows = useMemo(
    () => fields.filter(row => !row.parentRowId),
    [fields]
  );

  const nonDeletableRowIds = useMemo(
    () =>
      new Set(
        parentRows
          .filter(row =>
            MANDATORY_FIELD_NAMES.has(normalizeFieldName(row.field))
          )
          .map(row => row.id)
      ),
    [parentRows]
  );

  const dependentRowsByParentId = useMemo(() => {
    const mappedRows = new Map<string, ArticleTypeConfigField[]>();

    fields.forEach(row => {
      if (!row.parentRowId) return;

      const existing = mappedRows.get(row.parentRowId) ?? [];
      mappedRows.set(row.parentRowId, [...existing, row]);
    });

    return mappedRows;
  }, [fields]);

  const selectableRowIds = useMemo(
    () =>
      parentRows
        .filter(item => !nonDeletableRowIds.has(item.id))
        .map(item => item.id),
    [nonDeletableRowIds, parentRows]
  );

  const isAllSelected = useMemo(
    () =>
      selectableRowIds.length > 0 &&
      selectableRowIds.every(id => selectedIds.has(id)),
    [selectableRowIds, selectedIds]
  );

  const deletableSelectedIds = useMemo(
    () => Array.from(selectedIds).filter(id => !nonDeletableRowIds.has(id)),
    [nonDeletableRowIds, selectedIds]
  );

  const hasSelectedRows = deletableSelectedIds.length > 0;

  const editingRow = useMemo(
    () => fields.find(item => item.id === fieldModal.rowId),
    [fieldModal.rowId, fields]
  );

  const addableSourceFields = useMemo(() => {
    const existingNames = new Set(
      fields
        .filter(item => !item.parentRowId)
        .map(item => normalizeFieldName(item.field))
    );

    return dedupeSourceFieldOptions(allSourceFieldOptions).filter(option => {
      const normalized = normalizeFieldName(option.field);

      if (existingNames.has(normalized)) return false;

      const source = option.source;

      const hasExternalDependency = Boolean(
        source?.params &&
          typeof source.params === 'object' &&
          (source.params as any).externalFieldDependency?.path
      );

      const caseDataFields = (source as any)?.caseDataFields;
      const dependsOn = caseDataFields?.dependsOn?.toLowerCase?.();

      const isExistsDependency =
        dependsOn === 'exists' || dependsOn === 'exist';

      if (hasExternalDependency && !isExistsDependency) {
        return false;
      }

      const isDependent = isDependentField(source);
      if (isDependent && !isExistsDependency) {
        return false;
      }

      const parentFieldName = allDependentParentFieldByName.get(normalized);

      if (
        parentFieldName &&
        existingNames.has(normalizeFieldName(parentFieldName))
      ) {
        return false;
      }

      const hasParentPrefix = Array.from(existingNames).some(parent =>
        normalized.startsWith(parent)
      );

      if (hasParentPrefix) {
        return false;
      }

      return true;
    });
  }, [fields, allSourceFieldOptions, allDependentParentFieldByName]);

  const getRowIdsToDelete = (parentIds: string[]) => {
    const rowIdsToDelete = new Set<string>();

    parentIds.forEach(parentId => {
      rowIdsToDelete.add(parentId);

      const dependentIds =
        dependentRowsByParentId.get(parentId)?.map(item => item.id) ?? [];

      dependentIds.forEach(id => rowIdsToDelete.add(id));
    });

    return rowIdsToDelete;
  };

  const removeRowsFromState = (parentIds: string[]) => {
    const rowIdsToDelete = getRowIdsToDelete(parentIds);

    setFields(prev => prev.filter(item => !rowIdsToDelete.has(item.id)));

    setSelectedIds(prev => {
      const next = new Set(prev);
      rowIdsToDelete.forEach(id => next.delete(id));
      return next;
    });
  };

  const showDeletionBlockedToast = () => {
    toast({
      variant: 'error',
      title: 'Deletion is not allowed',
      description: 'This article type is associated with existing submissions.'
    });
  };

  const onToggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(selectableRowIds) : new Set());
  };

  const onToggleRow = (id: string, checked: boolean) => {
    if (nonDeletableRowIds.has(id)) return;

    setSelectedIds(prev => {
      const next = new Set(prev);

      if (checked) next.add(id);
      else next.delete(id);

      return next;
    });
  };

  const onOpenAddModal = () => {
    setFieldModal({ isOpen: true, mode: 'add' });
  };

  const onOpenEditModal = (rowId: string) => {
    setFieldModal({ isOpen: true, mode: 'edit', rowId });
  };

  const onCloseFieldModal = () => {
    setFieldModal({ isOpen: false, mode: 'add' });
  };

  const onDeleteSelected = () => {
    if (!deletableSelectedIds.length) return;

    setDeleteModal({
      isOpen: true,
      mode: 'bulk'
    });
  };

  const onDeleteRow = (rowId: string) => {
    const row = fields.find(f => f.id === rowId);
    if (!row) return;

    const isNewField = rowId.startsWith('field-');

    if (isNewField) {
      removeRowsFromState([rowId]);

      toast({
        variant: 'success',
        title: `${row.field} removed`
      });

      return;
    }

    setDeleteModal({
      isOpen: true,
      rowId,
      mode: 'single'
    });
  };

  const handleConfirmDelete = () => {
    if (hasAssociatedSubmissions) {
      showDeletionBlockedToast();
      setDeleteModal({ isOpen: false });
      return;
    }

    if (deleteModal.mode === 'bulk') {
      removeRowsFromState(deletableSelectedIds);
      setDeleteModal({ isOpen: false });

      toast({
        variant: 'success',
        title: 'Deletions Pending',
        description: `${deletableSelectedIds.length} row(s) removed. Save changes to delete permanently.`
      });

      return;
    }

    const rowId = deleteModal.rowId;
    if (!rowId) return;

    const row = fields.find(f => f.id === rowId);
    if (!row) return;

    removeRowsFromState([rowId]);
    setDeleteModal({ isOpen: false });

    toast({
      variant: 'success',
      title: 'Deletion Pending',
      description: `${row.field} removed. Save changes to delete permanently.`
    });
  };

  const onSaveField = (values: FieldEditorSubmit) => {
    const normalizedFieldName = normalizeFieldName(values.field);
    const isRequiredLockedByMandatoryField =
      MANDATORY_FIELD_NAMES.has(normalizedFieldName) &&
      normalizedFieldName !== EXTRACTION_SKIP_CHECKBOX_FIELD_NAME;

    const nextValues = isRequiredLockedByMandatoryField
      ? { ...values, required: true }
      : values;

    if (fieldModal.mode === 'edit' && fieldModal.rowId) {
      setFields(prev =>
        prev.map(item =>
          item.id === fieldModal.rowId ? { ...item, ...nextValues } : item
        )
      );
      onCloseFieldModal();
      return;
    }

    const hasDuplicateParentField = parentRows.some(
      row => normalizeFieldName(row.field) === normalizedFieldName
    );

    if (hasDuplicateParentField) {
      toast({
        variant: 'error',
        title: 'This field is already added to the table'
      });
      return;
    }

    const parentRowId = `field-${Date.now()}`;
    const dependentSourceFields =
      dependentSourceFieldsByParentField.get(normalizedFieldName) ?? [];

    const dependentRows = dependentSourceFields.map((option, index) => ({
      id: `${parentRowId}-dependent-${index}`,
      field: option.field,
      kind: option.kind,
      required: option.required,
      validations: option.validations,
      labels: option.labels,
      parentRowId
    }));

    setFields(prev => [
      ...prev,
      { id: parentRowId, ...nextValues },
      ...dependentRows
    ]);

    onCloseFieldModal();
  };

  const buildArticleTypeConfigPayload = () => {
    const payloadFields: Record<string, FormFieldDefinition> = {};

    fields.forEach(row => {
      const fieldKey = normalizeFieldName(row.field);
      const baseDefinition = sourceFieldDefinitionByName.get(fieldKey);

      if (!baseDefinition) {
        throw new Error(`Missing source schema for field "${row.field}"`);
      }

      payloadFields[row.field] = {
        ...(baseDefinition as unknown as Record<string, unknown>),
        name: row.field,
        params: {
          ...(baseDefinition.params as unknown as Record<string, unknown>),
          validations: {
            ...(row.validations as Record<string, unknown>),
            required: Boolean(row.required)
          },
          labels: {
            ...(row.labels as Record<string, unknown>)
          }
        }
      } as unknown as FormFieldDefinition;
    });

    return { fields: payloadFields };
  };

  const onSaveChanges = async () => {
    if (!activeSubmissionContextId || !articleType.id) {
      toast({
        variant: 'error',
        title: 'Unable to save article type configuration'
      });
      return;
    }

    try {
      const payload = buildArticleTypeConfigPayload();
      const response = await updateArticleTypeConfigMutation.mutateAsync(
        payload
      );

      if (!response.isSuccess) {
        const message =
          typeof response.message === 'string'
            ? response.message
            : 'Failed to save field configuration';

        toast({
          variant: 'error',
          title: message
        });
        return;
      }

      toast({
        variant: 'success',
        title: 'Field configuration saved'
      });

      onClose();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to save field configuration';

      toast({
        variant: 'error',
        title: message
      });
    }
  };

  return (
    <Overlay>
      <NavbarDashboard />
      <Layout width="100%" paddingY={0}>
        <Flex
          flexDirection="column"
          gap={4}
          px={{ base: 4, md: 14 }}
          backgroundColor="surfaceSecondaryFill"
        >
          <Flex
            justifyContent="space-between"
            py={{ base: 4, md: 8 }}
            flexDirection={{ base: 'column', md: 'row' }}
            gap={{ base: 3, md: 0 }}
          >
            <Flex gap={3} direction="column">
              <Text textStyle="headingSection">
                Configure Fields - {articleType.name}
              </Text>
              <Text textStyle="textBody">
                {articleType.description ||
                  'Manage field definitions, labels, and validation rules for this article type.'}
              </Text>
            </Flex>

            <Flex justifyContent="flex-end" gap={2}>
              <Button
                iconLeft={<FontAwesomeIcon icon={faPlus} />}
                onClick={onOpenAddModal}
              >
                Add Field
              </Button>

              <Button
                variant="caution"
                iconLeft={<FontAwesomeIcon icon={faTrash} />}
                onClick={onDeleteSelected}
                isDisabled={!hasSelectedRows}
                disabled={!hasSelectedRows}
              >
                Delete Selected({selectedIds.size})
              </Button>
            </Flex>
          </Flex>

          <FieldConfigTable
            isLoading={isDocumentSchemaLoading}
            parentRows={parentRows}
            dependentRowsByParentId={dependentRowsByParentId}
            fieldComponentByName={fieldComponentByName}
            nonDeletableRowIds={nonDeletableRowIds}
            selectedIds={selectedIds}
            isAllSelected={isAllSelected}
            canSelectAll={selectableRowIds.length > 0}
            onToggleAll={onToggleAll}
            onToggleRow={onToggleRow}
            onEditRow={onOpenEditModal}
            onDeleteParentRow={onDeleteRow}
          />
        </Flex>

        <DeleteFieldModal
          isOpen={deleteModal.isOpen}
          onClose={() => setDeleteModal({ isOpen: false })}
          onConfirm={handleConfirmDelete}
        />

        <AddFieldModal
          isOpen={fieldModal.isOpen && fieldModal.mode === 'add'}
          sourceFieldOptions={addableSourceFields}
          mandatoryFieldNames={MANDATORY_AUTHOR_SUBMISSION_FIELDS}
          onClose={onCloseFieldModal}
          onSave={onSaveField}
        />

        <EditFieldModal
          isOpen={fieldModal.isOpen && fieldModal.mode === 'edit'}
          defaultValues={editingRow}
          sourceFieldOptions={sourceFieldOptions}
          mandatoryFieldNames={MANDATORY_AUTHOR_SUBMISSION_FIELDS}
          onClose={onCloseFieldModal}
          onSave={onSaveField}
        />
      </Layout>
      <Flex
        padding={{ base: 3, md: 4 }}
        bottom={0}
        zIndex={1}
        justifyContent="space-between"
        position="sticky"
        bgColor="surfacePrimaryFill"
        marginX="auto"
      >
        <Flex
          width="100%"
          justifyContent="space-between"
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: 2, md: 0 }}
        >
          <ExitWithoutSavingWarningModal onClose={onClose} />

          <Button
            variant="primary"
            onClick={onSaveChanges}
            isLoading={updateArticleTypeConfigMutation.isPending}
            isDisabled={
              isDocumentSchemaLoading ||
              updateArticleTypeConfigMutation.isPending
            }
          >
            Save Changes
          </Button>
        </Flex>
      </Flex>
    </Overlay>
  );
};

export default ArticleTypeConfigModal;
